import { useState } from 'react'

export interface DropdownOptionsHookResult<T, P extends any[]> {
  isLoading: boolean
  fetchData: (...params: P) => any
  reset: () => void
  responseData: T[]
  responseError: any
}

export interface DropdownOptionsProps<P> {
  service: (params: P) => Promise<any>
}

/***
 * This hook is used to fetch options for SelectOption components.
 * @param service - A function that returns a promise resolving to the options data.
 * @returns An object containing the loading state, a function to fetch data, the response data, and any error that occurred.
 *
 * * Usage:
 * const { isLoading, fetchData, responseData } = useDropdownOptions<ResponseType, ParametersType[parameter1Name:number, parameter2Name: string]>(fetchOptions)
 *
 * * Example:  const profileDropdownOption = useDropdownOptions<ProfileModel, [number, number]>(fetchProfilesByLeadType)
 *
 * This will return an object with properties:
 * - isLoading: boolean indicating if the data is being fetched.
 * - fetchData: function to call the service and fetch data.
 * - responseData: array of options fetched from the service.
 * - responseError: any error that occurred during the fetch.
 */
export const useDropdownOptions = <T, P extends any[]>(
  service
): DropdownOptionsHookResult<T, P> => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [responseData, setResponseData] = useState<T[]>([])
  const [responseError, setResponseError] = useState<any>(undefined)

  const fetchData = async (...params) => {
    setIsLoading(true)
    await service(...params)
      .then(response => {
        if (response.success) {
          setResponseData(response.data)
        } else {
          setResponseError(response.error)
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const reset = () => {
    setIsLoading(false)
    setResponseData([])
    setResponseError(undefined)
  }

  return { isLoading, fetchData, reset, responseData, responseError }
}
